import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Users, DollarSign, TrendingUp, Settings, Save, RotateCcw, Edit3, AlertTriangle } from "lucide-react";

const DAY_OPTIONS = [
  { v: 1, label: "اليوم" },
  { v: 7, label: "7 أيام" },
  { v: 30, label: "30 يوم" },
];

export default function AdminUserUsage() {
  const [data, setData] = useState(null);
  const [settings, setSettings] = useState(null);
  const [days, setDays] = useState(1);
  const [showSettings, setShowSettings] = useState(false);
  const [overrideOpen, setOverrideOpen] = useState(null);
  const [sortKey, setSortKey] = useState("cost_today_usd"); // clickable column header
  const [sortDir, setSortDir] = useState("desc"); // desc | asc

  const load = () => api.get("/admin/user-usage", { params: { days } }).then((r) => setData(r.data));
  const loadSettings = () => api.get("/admin/user-usage/settings").then((r) => setSettings(r.data));

  useEffect(() => { load(); loadSettings(); /* eslint-disable-next-line */ }, [days]);

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir(sortDir === "desc" ? "asc" : "desc");
    else { setSortKey(key); setSortDir("desc"); }
  };

  const sortedUsers = data ? [...data.users].sort((a, b) => {
    const av = a[sortKey] ?? 0; const bv = b[sortKey] ?? 0;
    if (typeof av === "string") return sortDir === "desc" ? bv.localeCompare(av, "ar") : av.localeCompare(bv, "ar");
    return sortDir === "desc" ? bv - av : av - bv;
  }) : [];

  if (!data) return <div className="text-center text-foreground/50 py-16">جاري التحميل...</div>;

  return (
    <div className="space-y-6" data-testid="admin-user-usage-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-display font-semibold">استخدام الأعضاء</h1>
          <p className="text-foreground/60 text-sm mt-1">راقب استهلاك كل عضو ومدى قربه من الحد اليومي.</p>
        </div>
        <div className="flex gap-2 items-center">
          {DAY_OPTIONS.map((o) => (
            <Button key={o.v} size="sm" variant={days === o.v ? "default" : "outline"} onClick={() => setDays(o.v)} data-testid={`days-${o.v}`}>
              {o.label}
            </Button>
          ))}
          <Button size="sm" variant="outline" onClick={() => setShowSettings(true)} data-testid="btn-open-settings">
            <Settings className="h-4 w-4 me-1" /> الإعدادات
          </Button>
        </div>
      </div>

      {/* Platform-wide cost cards (today / this month / this year / lifetime) */}
      <div className="grid gap-3 grid-cols-2 sm:grid-cols-4">
        <MetricCard label="تكلفة اليوم" value={`$${data.platform_costs?.today ?? 0}`} icon={DollarSign} accent="bg-emerald-50 border-emerald-200 text-emerald-800" testid="cost-today" />
        <MetricCard label="تكلفة الشهر" value={`$${data.platform_costs?.month ?? 0}`} icon={DollarSign} accent="bg-blue-50 border-blue-200 text-blue-800" testid="cost-month" />
        <MetricCard label="تكلفة السنة" value={`$${data.platform_costs?.year ?? 0}`} icon={DollarSign} accent="bg-purple-50 border-purple-200 text-purple-800" testid="cost-year" />
        <MetricCard label="الإجمالي الكلي" value={`$${data.platform_costs?.lifetime ?? 0}`} icon={TrendingUp} accent="bg-amber-50 border-amber-200 text-amber-800" testid="cost-lifetime" />
      </div>

      {/* Window-specific summary (matches the days filter) */}
      <div className="grid gap-3 grid-cols-2 sm:grid-cols-3">
        <MetricCard label={days === 1 ? "تكلفة اليوم (الفترة)" : `تكلفة ${days} يوم`} value={`$${data.total_cost_usd}`} icon={DollarSign} accent="bg-foreground/5 border-foreground/10 text-foreground/80" />
        <MetricCard label="أعضاء نشطون" value={data.user_count} icon={Users} accent="bg-foreground/5 border-foreground/10 text-foreground/80" />
        <MetricCard label="متوسط تكلفة العضو" value={data.user_count > 0 ? `$${(data.total_cost_usd / data.user_count).toFixed(3)}` : "—"} icon={TrendingUp} accent="bg-foreground/5 border-foreground/10 text-foreground/80" />
      </div>

      {/* Users table */}
      <Card className="p-0 border-foreground/10 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="users-usage-table">
            <thead className="bg-foreground/5">
              <tr className="text-start">
                <SortableHeader label="العضو" k="full_name" cur={sortKey} dir={sortDir} onClick={toggleSort} />
                <SortableHeader label="شات اليوم" k="ai_messages" cur={sortKey} dir={sortDir} onClick={toggleSort} />
                <SortableHeader label="وصفات اليوم" k="recipe_imports" cur={sortKey} dir={sortDir} onClick={toggleSort} />
                <SortableHeader label="رفع" k="uploads_count" cur={sortKey} dir={sortDir} onClick={toggleSort} />
                <SortableHeader label="اليوم $" k="cost_today_usd" cur={sortKey} dir={sortDir} onClick={toggleSort} />
                <SortableHeader label="الشهر $" k="cost_month_usd" cur={sortKey} dir={sortDir} onClick={toggleSort} />
                <SortableHeader label="السنة $" k="cost_year_usd" cur={sortKey} dir={sortDir} onClick={toggleSort} />
                <SortableHeader label="الإجمالي $" k="lifetime_cost_usd" cur={sortKey} dir={sortDir} onClick={toggleSort} />
                <th className="p-3 text-start font-semibold"></th>
              </tr>
            </thead>
            <tbody>
              {sortedUsers.length === 0 && (
                <tr><td colSpan={9} className="p-8 text-center text-foreground/50">لا يوجد أعضاء</td></tr>
              )}
              {sortedUsers.map((u) => (
                <UserRow key={u.user_id} u={u} days={days} onEdit={() => setOverrideOpen(u)} />
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {showSettings && settings && (
        <SettingsModal
          settings={settings}
          onClose={() => setShowSettings(false)}
          onSaved={() => { setShowSettings(false); loadSettings(); load(); }}
        />
      )}
      {overrideOpen && (
        <OverrideModal
          user={overrideOpen}
          onClose={() => setOverrideOpen(null)}
          onSaved={() => { setOverrideOpen(null); load(); }}
        />
      )}
    </div>
  );
}

function MetricCard({ label, value, icon: Icon, accent, testid }) {
  return (
    <Card className={`p-4 border ${accent}`} data-testid={testid}>
      <div className="flex items-center gap-2 text-xs mb-1"><Icon className="h-3.5 w-3.5" /> {label}</div>
      <div className="text-2xl font-display font-bold">{value}</div>
    </Card>
  );
}

function SortableHeader({ label, k, cur, dir, onClick }) {
  const active = cur === k;
  return (
    <th className="p-3 text-start font-semibold cursor-pointer hover:bg-foreground/10 select-none" onClick={() => onClick(k)} data-testid={`sort-${k}`}>
      <span className="inline-flex items-center gap-1">
        {label}
        {active && <span className="text-primary text-[10px]">{dir === "desc" ? "▼" : "▲"}</span>}
      </span>
    </th>
  );
}

function UserRow({ u, days, onEdit }) {
  const critical = u.ai_pct >= 90 || u.recipes_pct >= 90;
  const warn = !critical && (u.ai_pct >= 70 || u.recipes_pct >= 70);
  return (
    <tr className={`border-t border-foreground/5 ${critical ? "bg-rose-50/40" : warn ? "bg-amber-50/40" : ""}`} data-testid={`user-row-${u.user_id}`}>
      <td className="p-3">
        <div className="font-medium">{u.full_name}</div>
        <div className="text-[11px] text-foreground/50">{u.email}</div>
        <div className="flex flex-wrap gap-1 mt-1">
          <TierBadge tier={u.tier} />
          <FunnelBadge funnel={u.source_funnel} />
          <SubStatusBadge status={u.subscription_status} periodEnd={u.current_period_end} />
          {u.has_override && <Badge variant="outline" className="text-[9px] bg-purple-50 border-purple-300 text-purple-800">حد مخصص</Badge>}
        </div>
      </td>
      <td className="p-3">
        <div className="font-semibold">{u.ai_messages}</div>
        {days === 1 && <ProgressPill pct={u.ai_pct} />}
      </td>
      <td className="p-3">
        <div className="font-semibold">{u.recipe_imports}</div>
        {days === 1 && <ProgressPill pct={u.recipes_pct} />}
      </td>
      <td className="p-3">
        <div className="font-semibold">{u.uploads_count}</div>
        <div className="text-[11px] text-foreground/50">{u.uploads_mb.toLocaleString("ar-EG")} م.ب</div>
      </td>
      <td className="p-3 text-emerald-700 font-semibold">${(u.cost_today_usd || 0).toFixed(3)}</td>
      <td className="p-3 text-blue-700 font-semibold">${(u.cost_month_usd || 0).toFixed(2)}</td>
      <td className="p-3 text-purple-700 font-semibold">${(u.cost_year_usd || 0).toFixed(2)}</td>
      <td className="p-3 text-amber-700 font-semibold">${(u.lifetime_cost_usd || 0).toFixed(2)}</td>
      <td className="p-3 text-end">
        <button onClick={onEdit} className="p-1.5 rounded hover:bg-foreground/10" data-testid={`btn-override-${u.user_id}`} aria-label="حدود مخصصة">
          <Edit3 className="h-3.5 w-3.5" />
        </button>
      </td>
    </tr>
  );
}

function ProgressPill({ pct }) {
  const p = Math.min(100, pct || 0);
  const color = p >= 90 ? "bg-rose-500" : p >= 70 ? "bg-amber-500" : "bg-emerald-500";
  return (
    <div className="mt-1 h-1 w-16 rounded-full bg-foreground/10 overflow-hidden">
      <div className={`h-full ${color}`} style={{ width: `${p}%` }} />
    </div>
  );
}

function TierBadge({ tier }) {
  const meta = {
    paid:         { label: "مدفوع",  cls: "bg-emerald-50 border-emerald-300 text-emerald-800" },
    free_forever: { label: "مجاني دائم", cls: "bg-blue-50 border-blue-300 text-blue-800" },
    blocked:      { label: "منتهي",  cls: "bg-rose-50 border-rose-300 text-rose-800" },
  }[tier] || { label: tier || "—", cls: "bg-foreground/5 border-foreground/20 text-foreground/70" };
  return (
    <Badge variant="outline" className={`text-[9px] ${meta.cls}`} data-testid={`tier-badge-${tier}`}>
      {meta.label}
    </Badge>
  );
}

// Marketing funnel badge — shows which landing page captured this member.
// `null` / missing funnel is treated as legacy pre-tagging data and hidden.
function FunnelBadge({ funnel }) {
  if (!funnel) return null;
  const meta = {
    flex:        { label: "المرونة",           cls: "bg-orange-50 border-orange-300 text-orange-800" },
    homeworkout: { label: "التمارين المنزلية", cls: "bg-indigo-50 border-indigo-300 text-indigo-800" },
  }[funnel] || { label: funnel, cls: "bg-foreground/5 border-foreground/20 text-foreground/70" };
  return (
    <Badge variant="outline" className={`text-[9px] ${meta.cls}`} data-testid={`funnel-badge-${funnel}`}>
      {meta.label}
    </Badge>
  );
}

// Subscription-status badge — highlights members whose access is ending soon.
// `canceling` = user turned off auto-renew, still has access until periodEnd.
// Any subscriber with a periodEnd within 7 days gets an orange "ينتهي قريباً" warning.
function SubStatusBadge({ status, periodEnd }) {
  if (!status) return null;
  const now = Date.now();
  const end = periodEnd ? Date.parse(periodEnd) : null;
  const daysLeft = end ? Math.max(0, Math.ceil((end - now) / 86400000)) : null;
  const meta = {
    active:    { label: "نشط",           cls: "bg-emerald-50 border-emerald-300 text-emerald-800" },
    trialing:  { label: "تجريبي",         cls: "bg-blue-50 border-blue-300 text-blue-800" },
    past_due:  { label: "دفع متأخر",       cls: "bg-amber-50 border-amber-300 text-amber-800" },
    canceling: { label: daysLeft != null ? `يلغى بعد ${daysLeft} يوم` : "قيد الإلغاء",
                 cls: "bg-orange-50 border-orange-300 text-orange-800" },
    canceled:  { label: "منتهي",          cls: "bg-rose-50 border-rose-300 text-rose-800" },
  }[status];
  if (!meta) return null;
  // Extra warning for active subs ending within a week (auto-renew edge cases).
  const expiring = status === "active" && daysLeft != null && daysLeft <= 7;
  return (
    <>
      <Badge variant="outline" className={`text-[9px] ${meta.cls}`} data-testid={`sub-badge-${status}`}>
        {meta.label}
      </Badge>
      {expiring && (
        <Badge variant="outline" className="text-[9px] bg-orange-50 border-orange-300 text-orange-800" data-testid="sub-expiring-soon">
          ينتهي خلال {daysLeft} يوم
        </Badge>
      )}
    </>
  );
}

function SettingsModal({ settings, onClose, onSaved }) {
  // settings.limits is now { paid: {...}, free_forever: {...} }
  const [limits, setLimits] = useState(settings.limits);
  const [costs, setCosts] = useState(settings.costs);
  const [saving, setSaving] = useState(false);

  const setTierLimit = (tier, key, value) => {
    setLimits((prev) => ({ ...prev, [tier]: { ...prev[tier], [key]: value } }));
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/admin/user-usage/settings/limits", {
        paid: limits.paid,
        free_forever: limits.free_forever,
      });
      await api.patch("/admin/user-usage/settings/costs", costs);
      toast.success("تم الحفظ");
      onSaved();
    } catch { toast.error("فشل الحفظ"); }
    finally { setSaving(false); }
  };

  const reset = () => {
    setLimits({
      paid: { ...settings.defaults.paid },
      free_forever: { ...settings.defaults.free_forever },
    });
    setCosts({ ...settings.default_costs });
  };

  const TIER_META = [
    { key: "paid",         label: "المشتركين المدفوعين", desc: "شهري + سنوي (النشط منهم)", color: "bg-emerald-50 border-emerald-300 text-emerald-800" },
    { key: "free_forever", label: "الأعضاء المجانيون (Free Forever)", desc: "الحسابات المجانية الدائمة", color: "bg-blue-50 border-blue-300 text-blue-800" },
  ];
  const LIMIT_LABELS = [
    ["ai_messages_daily",    "رسائل شات الذكاء / يوم"],
    ["recipe_imports_daily", "استيراد وصفات / يوم"],
    ["uploads_count_daily",  "عدد ملفات الرفع / يوم"],
    ["uploads_mb_daily",     "حجم الرفع بالميقا / يوم"],
  ];

  return (
    <div className="fixed inset-0 bg-black/50 z-50 grid place-items-center p-4" onClick={onClose}>
      <Card className="p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="settings-modal">
        <h2 className="text-xl font-display font-semibold mb-1 flex items-center gap-2">
          <Settings className="h-5 w-5" /> إعدادات الحدود والتكاليف
        </h2>
        <p className="text-xs text-foreground/60 mb-5 leading-relaxed">
          حدد سقفاً يومياً مختلفاً لكل فئة عضو. الأعضاء الذين انتهت اشتراكاتهم لن يستطيعوا استخدام أي ميزة حتى يجددوا.
        </p>

        {TIER_META.map((tier) => (
          <div key={tier.key} className="mb-5 rounded-lg border border-foreground/10 p-4" data-testid={`tier-block-${tier.key}`}>
            <div className="flex items-start justify-between gap-2 mb-3">
              <div>
                <Badge variant="outline" className={`text-xs ${tier.color}`}>{tier.label}</Badge>
                <div className="text-[11px] text-foreground/50 mt-1">{tier.desc}</div>
              </div>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              {LIMIT_LABELS.map(([k, label]) => (
                <LabeledInput
                  key={`${tier.key}-${k}`}
                  label={label}
                  value={(limits[tier.key] || {})[k] ?? 0}
                  onChange={(v) => setTierLimit(tier.key, k, v)}
                  testid={`limit-${tier.key}-${k}`}
                />
              ))}
            </div>
          </div>
        ))}

        <div className="font-semibold text-sm mt-2 mb-2">تكاليف الوحدة ($ لكل استخدام)</div>
        <div className="grid gap-3 sm:grid-cols-2">
          {[
            ["ai_message_usd", "رسالة ذكاء واحدة"],
            ["recipe_import_usd", "استيراد وصفة واحدة"],
            ["upload_mb_usd", "رفع 1 ميقا"],
            ["stream_minute_usd", "دقيقة Stream مخزّنة"],
          ].map(([k, label]) => (
            <LabeledInput key={k} label={label} value={costs[k] ?? 0} onChange={(v) => setCosts({ ...costs, [k]: v })} step="0.0001" testid={`cost-${k}`} />
          ))}
        </div>

        <div className="flex gap-2 mt-6 flex-wrap">
          <Button onClick={save} disabled={saving} className="gap-1" data-testid="btn-save-settings"><Save className="h-4 w-4" /> حفظ</Button>
          <Button variant="outline" onClick={reset}><RotateCcw className="h-4 w-4 me-1" /> استعادة الافتراضي</Button>
          <Button variant="ghost" onClick={onClose}>إلغاء</Button>
        </div>
      </Card>
    </div>
  );
}

function OverrideModal({ user, onClose, onSaved }) {
  const [vals, setVals] = useState({
    ai_messages_daily: user.limits.ai_messages_daily,
    recipe_imports_daily: user.limits.recipe_imports_daily,
    uploads_count_daily: user.limits.uploads_count_daily,
    uploads_mb_daily: user.limits.uploads_mb_daily,
    reason: user.override_reason || "",
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/admin/user-usage/users/${user.user_id}/override`, vals);
      toast.success("تم حفظ الحد المخصص");
      onSaved();
    } catch { toast.error("فشل"); }
    finally { setSaving(false); }
  };
  const clearOverride = async () => {
    if (!window.confirm("مسح الحد المخصص وإرجاعه للحد الافتراضي؟")) return;
    await api.delete(`/admin/user-usage/users/${user.user_id}/override`);
    toast.success("تمت الإزالة");
    onSaved();
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 grid place-items-center p-4" onClick={onClose}>
      <Card className="p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()} data-testid="override-modal">
        <h2 className="text-lg font-display font-semibold mb-1">حد مخصص لـ {user.full_name}</h2>
        <p className="text-xs text-foreground/50 mb-4">{user.email}</p>

        {[
          ["ai_messages_daily", "رسائل شات الذكاء / يوم"],
          ["recipe_imports_daily", "استيراد وصفات / يوم"],
          ["uploads_count_daily", "عدد ملفات الرفع / يوم"],
          ["uploads_mb_daily", "حجم الرفع بالميقا / يوم"],
        ].map(([k, label]) => (
          <LabeledInput key={k} label={label} value={vals[k] ?? 0} onChange={(v) => setVals({ ...vals, [k]: v })} testid={`override-${k}`} />
        ))}
        <label className="text-xs font-semibold text-foreground/70 mb-1 block mt-3">سبب التخصيص (اختياري):</label>
        <Input value={vals.reason} onChange={(e) => setVals({ ...vals, reason: e.target.value })} placeholder="مثال: عضو VIP، إساءة استخدام..." data-testid="override-reason" />

        <div className="flex gap-2 mt-5 flex-wrap">
          <Button onClick={save} disabled={saving} className="gap-1"><Save className="h-4 w-4" /> حفظ</Button>
          {user.has_override && <Button variant="destructive" onClick={clearOverride}>إزالة الحد المخصص</Button>}
          <Button variant="ghost" onClick={onClose}>إلغاء</Button>
        </div>
      </Card>
    </div>
  );
}

function LabeledInput({ label, value, onChange, testid, step }) {
  return (
    <div className="flex items-center gap-2 justify-between">
      <label className="text-sm text-foreground/80 flex-1 min-w-0">{label}</label>
      <Input
        type="number"
        step={step || "1"}
        value={value}
        onChange={(e) => onChange(step ? parseFloat(e.target.value) || 0 : parseInt(e.target.value) || 0)}
        className="w-32 text-end"
        data-testid={testid}
      />
    </div>
  );
}
